import {emitCustomEvent} from '@/api/repositories/CustomEventRepository';
import {HubSpotCustomEventName} from '@/api/models/customEvents/hubSpotCustomEventName';
import {visualizeTrackingEvent} from './eventVisualization';

export enum LicenseActivity {
    NavigationHeatmapsLocked = 'Navigation: Office-Auslastung (gesperrt)',
    NavigationBookingRulesLocked = 'Navigation: Buchungsregeln (gesperrt)',
    NavigationUserTagsLocked = 'Navigation: User-Tags (gesperrt)',
    NavigationApiKeysLocked = 'Navigation: API-Keys (gesperrt)',
    NavigationCalendarLocked = 'Navigation: Kalender (gesperrt)',
    NavigationHrIntegrationLocked = 'Navigation: HR-Integration (gesperrt)',
    NavigationDesklyConnectLocked = 'Navigation: deskly Connect (gesperrt)',

    PaymentIntervalMonthly = 'Zahlungsintervall: Monatlich gewählt',
    PaymentIntervalYearly = 'Zahlungsintervall: Jährlich gewählt',
    PlanCorporateSelected = 'Plan-Auswahl: Corporate angeklickt',
    PlanEnterpriseSelected = 'Plan-Auswahl: Enterprise angeklickt',
    EnterprisePlusQuoteRequested = 'Enterprise-Plus-Angebot angefordert',
    LicenseRequestOver200Users = 'Lizenzanfrage über 200 Nutzer ausgelöst',

    LicenseFormOpenedFromOverview = 'Lizenz-Formular geöffnet (Lizenz-Übersicht)',
    LicenseFormOpenedFromBuyDialog = 'Lizenz-Formular geöffnet (Kauf-Dialog)',
    StripePortalLinkClicked = 'Stripe-Portal Link geklickt',
    StripePortalButtonClicked = 'Stripe-Portal Button geklickt',
    ExpertCallRequested = 'Expertengespräch angefordert',
}

export function trackLicenseActivity(activity: LicenseActivity): void {
    visualizeTrackingEvent('Event (Lizenz-Aktivität, Frontend)', activity);

    void emitCustomEvent(HubSpotCustomEventName.LicenseActivity, {activity_name: activity}).catch(
        () => {}
    );
}

type BackendLicenseActivityRoute = {
    method: string;
    pattern: RegExp;
    label: string | ((match: RegExpMatchArray, body: unknown) => string);
};

// Mirrors the license_activity custom events emitted by the backend LicenseActivityTracker
// (labels in src/Service/Tracking/LicenseActivityLabel.php). The backend emits them inside
// the API endpoints matched below, so the frontend requests to those endpoints are used
// to visualize the activities as toasts.
const BACKEND_LICENSE_ACTIVITY_ROUTES: BackendLicenseActivityRoute[] = [
    {
        method: 'GET',
        pattern:
            /^\/api\/admin\/subscription\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/,
        label: 'Lizenzübersicht aufgerufen',
    },
    {
        method: 'GET',
        pattern: /^\/api\/admin\/subscription-prices$/,
        label: 'Preistabelle aufgerufen',
    },
    {
        method: 'GET',
        pattern: /^\/api\/admin\/subscription\/licenses\/[^/]+\/cost(?:\/\d+)?$/,
        label: 'Lizenzkosten angefragt',
    },
    {
        method: 'POST',
        pattern: /^\/api\/admin\/subscription\/edit\/[^/]+$/,
        label: (_match, body) =>
            body instanceof FormData && body.get('subscription[assignAutomatically]') === 'true'
                ? 'Automatische Lizenzvergabe aktiviert'
                : 'Automatische Lizenzvergabe deaktiviert',
    },
    {
        method: 'PUT',
        pattern: /^\/api\/admin\/subscription\/licenses\/[^/]+\/increase\/(\d+)$/,
        label: match => {
            const numberOfLicenses = Number(match[1]);

            return numberOfLicenses === 1
                ? '1 Lizenz per Self-Service gekauft'
                : `${numberOfLicenses} Lizenzen per Self-Service gekauft`;
        },
    },
    {
        method: 'POST',
        pattern: /^\/api\/admin\/payment\/checkout$/,
        label: 'Checkout gestartet',
    },
];

export function matchBackendLicenseActivity(
    method: string,
    url: string,
    body: unknown
): string | null {
    for (const route of BACKEND_LICENSE_ACTIVITY_ROUTES) {
        if (route.method !== method) {
            continue;
        }

        const match = url.match(route.pattern);
        if (!match) {
            continue;
        }

        return typeof route.label === 'string' ? route.label : route.label(match, body);
    }

    return null;
}

export function visualizeBackendLicenseActivityRequest(
    method: string,
    url: string,
    body: unknown
): void {
    const label = matchBackendLicenseActivity(method, url, body);

    if (label !== null) {
        visualizeTrackingEvent('Event (Lizenz-Aktivität, Backend)', label);
    }
}
